import { useEffect, useState } from "react";
import api from "../lib/api";
import { inr, DOCTOR_STAGES, CLINICIAN_STAGES, PATIENT_STAGES, LEAD_STAGES, LOST_REASONS, PAYMENT_METHODS, labelOf } from "../lib/constants";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import { TrendingUp, Activity, PieChart as PieIcon, Filter, Target, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const METHOD_SERIES = [
  ...PAYMENT_METHODS.map((m, i) => ({ ...m, color: ["#2540FF", "#0D9488", "#D97706", "#0A1E3F"][i] })),
  { key: "unspecified", label: "Not recorded", color: "#cbd5e1" },
];

const Card = ({ title, sub, icon: Icon, children, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <div className="mb-4">
      <h3 className="font-semibold font-head text-ccc-navy flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-ccc-blue" />}{title}</h3>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
    {children}
  </div>
);

const FunnelBars = ({ funnel, stages, color }) => {
  const total = funnel.total || 1;
  return (
    <div className="space-y-2">
      {stages.map((s) => {
        const c = funnel.counts[s.key] || 0;
        return (
          <div key={s.key} className="flex items-center gap-2 text-xs">
            <span className="w-32 truncate text-slate-500">{s.label}</span>
            <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
              <div className="h-full" style={{ width: `${(c / total) * 100}%`, background: color }} />
            </div>
            <span className="w-8 text-right font-semibold text-ccc-navy">{c}</span>
          </div>
        );
      })}
      <div className="pt-2 text-xs text-slate-500 flex justify-between border-t border-slate-100 mt-2">
        <span>Total {funnel.total}</span><span className="text-ccc-emerald font-semibold">Conv {funnel.conversion_pct}%</span>
      </div>
    </div>
  );
};

const PIE_COLORS = ["#2540FF", "#D97706"];

export default function Reports() {
  const [ov, setOv] = useState(null);
  const [ramp, setRamp] = useState(null);
  const [util, setUtil] = useState(null);
  const [sources, setSources] = useState(null);
  const [pv, setPv] = useState(null);
  const [rev, setRev] = useState(null);
  const [lost, setLost] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const downloadCollections = async () => {
    try {
      const r = await api.get("/reports/collections.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "correcore_collections.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the sheet"); }
  };
  const [targets, setTargets] = useState({ doctor_start: 40, doctor_target: 160 });

  useEffect(() => {
    api.get("/reports/overview").then((r) => setOv(r.data));
    api.get("/reports/doctor-ramp").then((r) => setRamp(r.data));
    api.get("/reports/clinician-utilization").then((r) => setUtil(r.data));
    api.get("/reports/patient-sources").then((r) => setSources(r.data));
    api.get("/reports/pipeline-value").then((r) => setPv(r.data));
    api.get("/reports/revenue").then((r) => setRev(r.data));
    api.get("/reports/lost-reasons").then((r) => setLost(r.data));
    api.get("/settings/targets").then((r) => setTargets(r.data));
  }, []);

  const saveTargets = async () => {
    const { data } = await api.put("/settings/targets", {
      doctor_start: Number(targets.doctor_start) || 0,
      doctor_target: Number(targets.doctor_target) || 0,
    });
    setTargets(data);
    toast.success("Targets updated");
    api.get("/reports/doctor-ramp").then((r) => setRamp(r.data));
    api.get("/reports/pipeline-value").then((r) => setPv(r.data));
    api.get("/reports/overview").then((r) => setOv(r.data));
  };

  if (!ov) return <div className="text-slate-400">Loading reports…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy" data-testid="reports-title">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Funnel conversion, doctor ramp, clinician utilization & acquisition sources.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-end gap-4" data-testid="targets-editor">
        <div>
          <Label className="text-xs">Ramp start</Label>
          <Input type="number" data-testid="ramp-start-input" value={targets.doctor_start} onChange={(e) => setTargets({ ...targets, doctor_start: e.target.value })} className="mt-1 w-28" />
        </div>
        <div>
          <Label className="text-xs">Ramp target</Label>
          <Input type="number" data-testid="ramp-target-input" value={targets.doctor_target} onChange={(e) => setTargets({ ...targets, doctor_target: e.target.value })} className="mt-1 w-28" />
        </div>
        <Button data-testid="save-targets-button" onClick={saveTargets} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save targets</Button>
        <span className="text-xs text-slate-400 ml-auto self-center">Editable — drives the ramp chart, KPIs & forecast</span>
      </div>

      <Card title="Doctor Ramp-to-Target" sub={`Actual active doctors vs ${targets.doctor_start} → ${targets.doctor_target} Month-12 target`} icon={TrendingUp} testid="report-ramp">
        {ramp && (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={ramp.series}>
              <defs>
                <linearGradient id="rf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2540FF" stopOpacity={0.3} /><stop offset="100%" stopColor="#2540FF" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="actual" name="Actual active" stroke="#2540FF" strokeWidth={2.5} fill="url(#rf)" />
              <Line type="monotone" dataKey="target" name="Target" stroke="#D97706" strokeWidth={2} strokeDasharray="6 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Sales Pipeline — Cold Lead → Converted" sub="Top-of-funnel sales motion across all lead types" icon={Filter} testid="report-lead-funnel">
        <FunnelBars funnel={ov.lead_funnel} stages={LEAD_STAGES} color="#10B981" />
      </Card>

      {pv && (
        <Card title="Pipeline Value & Forecast" sub="Expected conversions and win-rate by relationship owner" icon={Target} testid="report-pipeline-value">
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs text-slate-500">Expected conversions (weighted)</p>
              <p className="text-2xl font-bold font-head text-ccc-blue mt-1">{pv.total_expected_conversions}</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <p className="text-xs text-slate-500">Expected new doctors</p>
              <p className="text-2xl font-bold font-head text-ccc-amber mt-1">+{pv.total_expected_doctors}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <p className="text-xs text-slate-500">Doctor ramp forecast</p>
              <p className="text-2xl font-bold font-head text-ccc-emerald mt-1">{pv.forecast_doctors} <span className="text-sm font-normal text-slate-400">/ {pv.target}</span></p>
              <p className="text-[11px] text-slate-400 mt-0.5">{pv.active_doctors} active + pipeline</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Owner</th>
                  <th className="py-2 px-3 text-right">Open</th>
                  <th className="py-2 px-3 text-right">Converted</th>
                  <th className="py-2 px-3 text-right">Lost</th>
                  <th className="py-2 px-3 text-right">Win rate</th>
                  <th className="py-2 pl-3 text-right">Exp. conv.</th>
                </tr>
              </thead>
              <tbody>
                {pv.rows.map((r) => (
                  <tr key={r.owner} className="border-b border-slate-50">
                    <td className="py-2.5 pr-3 font-medium text-ccc-navy">{r.owner}</td>
                    <td className="py-2.5 px-3 text-right text-slate-600">{r.open}</td>
                    <td className="py-2.5 px-3 text-right text-ccc-emerald font-semibold">{r.converted}</td>
                    <td className="py-2.5 px-3 text-right text-slate-400">{r.lost}</td>
                    <td className="py-2.5 px-3 text-right">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden hidden sm:inline-block">
                          <span className="block h-full bg-ccc-blue" style={{ width: `${r.win_rate}%` }} />
                        </span>
                        <b className="text-ccc-navy">{r.win_rate}%</b>
                      </span>
                    </td>
                    <td className="py-2.5 pl-3 text-right font-mono text-ccc-blue">{r.expected_conversions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {rev && (
        <Card title="Revenue Rollup" sub="Booked vs collected revenue from each patient's per-session rate" icon={Target} testid="report-revenue">
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4"><p className="text-xs text-slate-500">Booked revenue</p><p className="text-2xl font-bold font-head text-ccc-blue mt-1">{inr(rev.total_expected)}</p></div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4"><p className="text-xs text-slate-500">Collected</p><p className="text-2xl font-bold font-head text-ccc-emerald mt-1">{inr(rev.collected)}</p></div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4"><p className="text-xs text-slate-500">Outstanding</p><p className="text-2xl font-bold font-head text-ccc-amber mt-1">{inr(rev.outstanding)}</p></div>
          </div>
          {rev.by_doctor.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Booked revenue by originating doctor</p>
              <div className="space-y-2">
                {rev.by_doctor.map((r) => (
                  <div key={r.doctor} className="flex items-center gap-2 text-sm">
                    <span className="w-40 truncate text-slate-600">{r.doctor}</span>
                    <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-ccc-blue" style={{ width: `${(r.revenue / (rev.by_doctor[0].revenue || 1)) * 100}%` }} /></div>
                    <span className="w-24 text-right font-mono text-ccc-navy">{inr(r.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-xs text-slate-400">No patient revenue yet.</p>}
          {rev.trend && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Booked vs collected — last 12 months</p>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={rev.trend}>
                  <defs>
                    <linearGradient id="expFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2540FF" stopOpacity={0.25} /><stop offset="100%" stopColor="#2540FF" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => inr(v)} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="expected" name="Booked" stroke="#2540FF" strokeWidth={2.5} fill="url(#expFill)" />
                  <Line type="monotone" dataKey="collected" name="Collected" stroke="#10B981" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      )}

      {rev?.by_method && (
        <Card title="Collections by Payment Method" sub="Collected revenue per month, grouped by paid date" icon={TrendingUp} testid="report-collections-by-method">
          {isAdmin && (
            <div className="flex justify-end -mt-12 mb-6">
              <Button size="sm" variant="outline" onClick={downloadCollections} data-testid="collections-export-button"><Download className="h-4 w-4 mr-1.5" />Download Excel</Button>
            </div>
          )}
          <div className="flex flex-wrap gap-3 mb-4">
            {METHOD_SERIES.map((m) => (
              <div key={m.key} className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2" data-testid={`collections-total-${m.key}`}>
                <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: m.color }} />{m.label}</p>
                <p className="text-sm font-bold font-head text-ccc-navy">{inr(rev.by_method.reduce((s, r) => s + (r[m.key] || 0), 0))}</p>
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={rev.by_method}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => inr(v)} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend />
              {METHOD_SERIES.map((m, i) => (
                <Bar key={m.key} dataKey={m.key} name={m.label} stackId="pay" fill={m.color} radius={i === METHOD_SERIES.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]} barSize={26} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Doctor Funnel" icon={Filter} testid="report-doctor-funnel"><FunnelBars funnel={ov.doctor_funnel} stages={DOCTOR_STAGES} color="#D97706" /></Card>
        <Card title="Clinician Funnel" icon={Filter} testid="report-clinician-funnel"><FunnelBars funnel={ov.clinician_funnel} stages={CLINICIAN_STAGES} color="#0D9488" /></Card>
        <Card title="Patient Funnel" icon={Filter} testid="report-patient-funnel"><FunnelBars funnel={ov.patient_funnel} stages={PATIENT_STAGES} color="#2540FF" /></Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Patient Acquisition Source" sub="Clinician-order vs direct/marketing" icon={PieIcon} testid="report-sources">
          {sources && (
            <div className="grid grid-cols-2 gap-2 items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={sources.by_source} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                    {sources.by_source.map((e, i) => <Cell key={`${e.name}-${i}`} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {sources.by_source.map((s, i) => (
                  <div key={s.name}>
                    <div className="flex items-center gap-2"><span className="h-3 w-3 rounded" style={{ background: PIE_COLORS[i] }} /><span className="text-xs text-slate-500">{s.name}</span></div>
                    <p className="text-xl font-bold font-head text-ccc-navy ml-5">{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sources?.top_doctors?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Top Referring Doctors</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={sources.top_doctors} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#D97706" radius={[0, 6, 6, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Clinician Utilization" sub={`Avg ${util?.avg_utilization ?? 0}% of weekly capacity`} icon={Activity} testid="report-utilization">
          <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
            {util?.rows.map((r) => (
              <div key={r.id}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-ccc-navy font-medium">{r.name} <span className="text-xs text-slate-400">· {r.city}</span></span>
                  <span className="font-semibold text-ccc-teal">{r.utilization_pct}%</span>
                </div>
                <Progress value={Math.min(r.utilization_pct, 100)} className="h-2" />
                <p className="text-[11px] text-slate-400 mt-0.5">{r.active_cases}/{r.capacity} sessions</p>
              </div>
            ))}
            {(!util || util.rows.length === 0) && <p className="text-xs text-slate-400">No active clinicians yet.</p>}
          </div>
        </Card>

        <Card title="Why Patients Are Lost" sub={`${lost?.total ?? 0} lost enquiries`} icon={PieIcon} testid="report-lost-reasons">
          <div className="space-y-2">
            {lost?.rows.map((r) => (
              <div key={r.reason} className="flex items-center gap-2 text-xs" data-testid={`lost-reason-bar-${r.reason}`}>
                <span className="w-40 truncate text-slate-500">{r.reason === "unspecified" ? "Not recorded" : labelOf(LOST_REASONS, r.reason)}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-rose-400" style={{ width: `${(r.count / (lost.total || 1)) * 100}%` }} /></div>
                <span className="w-8 text-right font-semibold text-ccc-navy">{r.count}</span>
              </div>
            ))}
            {(!lost || lost.total === 0) && <p className="text-xs text-slate-400">No lost patients yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
